import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, formatApiError, formatMinutes } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from "recharts";
import { Loader2, Trophy, TrendingDown } from "lucide-react";

const METRICS = {
  visites: { label: "Visites", color: "#1D4ED8" },
  prospection_minutes: { label: "Minutes prospection", color: "#16A34A" },
  relances_vendeurs: { label: "Relances vendeurs", color: "#F59E0B" },
  signatures_en_cours: { label: "Signatures en cours", color: "#DC2626" },
  compromis_en_cours: { label: "Compromis en cours", color: "#7C3AED" },
};

export default function AdminStats() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("visites");
  const [compare, setCompare] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/stats/overview");
        setOverview(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const metrics = METRICS;

  const ranked = useMemo(() => {
    if (!overview) return [];
    return [...overview.per_user].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  }, [overview, metric]);

  const max = Math.max(1, ...(ranked.map((u) => u[metric] || 0)));

  const radarData = useMemo(() => {
    if (!overview || !compare) return [];
    const target = overview.per_user.find((u) => u.user_id === compare);
    if (!target) return [];
    const avg = {};
    Object.keys(METRICS).forEach((k) => {
      const vals = overview.per_user.map((u) => u[k] || 0);
      avg[k] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    });
    const norm = (val, key) => {
      const maxVal = Math.max(1, ...overview.per_user.map((u) => u[key] || 0));
      return Math.round((val / maxVal) * 100);
    };
    return Object.keys(METRICS).map((k) => ({
      metric: METRICS[k].label,
      [target.name]: norm(target[k] || 0, k),
      "Moyenne équipe": norm(avg[k], k),
    }));
  }, [overview, compare]);

  if (loading || !overview) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-8" data-testid="admin-stats">
        <p className="label-eyebrow mb-2">Analyse comparative</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Statistiques</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Comparez le travail effectué par chacun. Idéal pour illustrer la différence d'activité lors d'un point avec un négociateur.
        </p>
      </div>

      {/* Ranking */}
      <Card className="p-6 mb-8" data-testid="ranking-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <p className="label-eyebrow mb-1">Classement</p>
            <h2 className="font-display font-bold text-2xl">Qui produit le plus ?</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Indicateur</span>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="w-[220px]" data-testid="metric-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(metrics).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {ranked.length === 0 ? (
          <div className="text-center py-10 text-slate-500">Aucune donnée disponible.</div>
        ) : (
          <div className="space-y-3">
            {ranked.map((u, idx) => {
              const val = u[metric] || 0;
              const pct = (val / max) * 100;
              const display = metric === "prospection_minutes" ? formatMinutes(val) : val;
              return (
                <div key={u.user_id} className="flex items-center gap-4" data-testid={`rank-row-${u.user_id}`}>
                  <div className="w-10 text-center">
                    {idx === 0 ? (
                      <div className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-amber-100 text-amber-700">
                        <Trophy className="h-4 w-4" />
                      </div>
                    ) : (
                      <div className="text-sm font-semibold text-slate-400">#{idx + 1}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between mb-1.5">
                      <div className="font-semibold text-sm text-slate-900 truncate">{u.name}</div>
                      <div className="font-display font-bold text-sm tabular-nums">{display}</div>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: metrics[metric].color }}
                      />
                    </div>
                  </div>
                  {idx === ranked.length - 1 && ranked.length > 1 ? (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 gap-1">
                      <TrendingDown className="h-3 w-3" /> À accompagner
                    </Badge>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Bar chart absolute values */}
      <Card className="p-6 mb-8">
        <div className="mb-4">
          <p className="label-eyebrow mb-1">Vue absolue</p>
          <h2 className="font-display font-bold text-2xl">{metrics[metric].label} par négociateur</h2>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ranked.map((u) => ({ name: u.name.split(" ")[0], value: u[metric] || 0 }))}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 12 }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
              <YAxis tick={{ fill: "#475569", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" name={metrics[metric].label} fill={metrics[metric].color} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Radar compare */}
      <Card className="p-6" data-testid="radar-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <p className="label-eyebrow mb-1">Vs. moyenne équipe</p>
            <h2 className="font-display font-bold text-2xl">Radar comparatif</h2>
            <p className="text-sm text-slate-500 mt-1">Comparez un négociateur à la moyenne (échelle normalisée sur 100).</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Négociateur</span>
            <Select value={compare} onValueChange={setCompare}>
              <SelectTrigger className="w-[240px]" data-testid="radar-user-select">
                <SelectValue placeholder="Choisir un négociateur" />
              </SelectTrigger>
              <SelectContent>
                {overview.per_user.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {radarData.length === 0 ? (
          <div className="text-center py-14 text-slate-500">Sélectionnez un négociateur pour voir sa performance vs. la moyenne équipe.</div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis dataKey="metric" tick={{ fill: "#475569", fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <Radar name={overview.per_user.find((u) => u.user_id === compare)?.name} dataKey={overview.per_user.find((u) => u.user_id === compare)?.name} stroke="#1D4ED8" fill="#1D4ED8" fillOpacity={0.35} />
                <Radar name="Moyenne équipe" dataKey="Moyenne équipe" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.2} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </Layout>
  );
}
